import { useContext, useEffect, useMemo } from 'react'

import {
  Box,
  Button,
  Input,
  Modal,
  Text,
  TextArea,
} from '@fairfashion/design-system'
import { useForm } from 'react-hook-form'
import { useTheme } from 'styled-components'

import { AuthContext } from '../../contexts/auth'
import { IItemMetadata } from '../../pages/OriginItems/origin-items.types'
import { TagsInput } from '../../pages/ProviderInvoice/components/TagsInput/tags-input.comp'
import { CompanySegment } from '../../sdk/common/company.enum'

import { IItemForm, ItemFormProps } from './item-form.types'

export const ItemForm = ({
  itemCode,
  onSubmit,
  open,
  setOpen,
  data,
}: ItemFormProps) => {
  const { user } = useContext(AuthContext)

  const { register, handleSubmit, getValues, setValue, reset, watch } =
    useForm<IItemForm>({
      defaultValues: {} as IItemForm,
    })

  const theme = useTheme()

  const loadFormInEditMode = () => {
    const currentData: Partial<IItemForm> = {
      description: data?.description ?? '',
      quantity: data?.quantity,
      taxUnit: data?.taxUnit ?? '',
      code: itemCode ?? data?.code ?? '',
      metadata: data?.metadata,
    }
    reset(currentData)
  }

  useEffect(() => {
    if (open) {
      loadFormInEditMode()
    }
  }, [open])

  const providerIsFarm = useMemo(
    () =>
      data?.itemProviderRelation?.companyProviderSegment ===
      CompanySegment.FARM,
    [data],
  )

  const onSubmitItem = () => {
    let metadata: IItemMetadata | undefined = undefined

    const body = getValues()

    if (
      providerIsFarm &&
      (body.metadata?.cattleData?.length ||
        body.metadata?.transportationData?.length)
    ) {
      metadata = body.metadata
    }

    onSubmit({
      description: body.description,
      quantity: body.quantity,
      taxUnit: body.taxUnit,
      code: body.code,
      metadata,
    })

    setOpen(false)
  }

  const isEmpty = useMemo(() => {
    return (
      !data?.description && !data?.code && !data?.quantity && !data?.taxUnit
    )
  }, [data])

  const transportationData = watch('metadata.transportationData')
  const cattleData = watch('metadata.cattleData')

  return (
    <>
      {!isEmpty && (
        <Box
          width="100%"
          border={{ width: 1, color: 'grey_4' }}
          borderRadius={theme.border_radius.card}
          padding={2}
        >
          <Box width="100%" justifyContent="space-between">
            <Box direction="column">
              <Text size="small" color="grey_3">
                Descrição
              </Text>
              <Text size="small" fontWeight={500}>
                {data?.description ?? ''}
              </Text>
            </Box>
            <Box direction="column">
              <Text size="small" color="grey_3">
                Código SKU
              </Text>
              <Text size="small" fontWeight={500}>
                {data?.code}
              </Text>
            </Box>

            <Box direction="column">
              <Text size="small" color="grey_3">
                Quantidade
              </Text>
              <Text size="small" fontWeight={500}>
                {data?.quantity}
              </Text>
            </Box>
            <Box direction="column">
              <Text size="small" color="grey_3">
                Unidade
              </Text>
              <Text size="small" fontWeight={500}>
                {data?.taxUnit}
              </Text>
            </Box>
          </Box>
        </Box>
      )}
      <Modal
        open={open}
        onClose={setOpen}
        maxWidth="830px"
        width="100%"
        customTitle={
          <Text textTransform="uppercase" size="small" color="grey_3">
            Adicionar um Item
          </Text>
        }
      >
        <Box width="100%" direction="column" padding={1}>
          <form onSubmit={handleSubmit(onSubmitItem)}>
            <Box direction="column" width="100%" gap={2}>
              <Box gap={2} width="100%" flexWrap="wrap">
                <Box flex={1} minWidth="180px">
                  <Input
                    {...register('code')}
                    label="Código/SKU*"
                    disabled={!!itemCode}
                  />
                </Box>
              </Box>
              <Box gap={2} width="100%" flexWrap="wrap">
                <Box flex={1} minWidth="180px">
                  <Input
                    type="number"
                    {...register('quantity')}
                    label="Quantidade*"
                    placeholder="Quantidade"
                  />
                </Box>
                <Box flex={1} minWidth="180px">
                  <Input
                    {...register('taxUnit')}
                    label="Unidade*"
                    placeholder="Unidade"
                  />
                </Box>
              </Box>
              <Box gap={2} width="100%" flexWrap="wrap">
                <Box flex={1} minWidth="180px">
                  <TextArea
                    {...register('description')}
                    label="Descrição*"
                    placeholder="Descrição"
                  />
                </Box>
              </Box>

              {providerIsFarm && user?.supplyChains?.includes('leather') && (
                <Box gap={2} width="100%" flexWrap="wrap">
                  <Box flex={1}>
                    <TagsInput
                      label="Números de GTA"
                      placeholder="--"
                      onChange={(_value) =>
                        setValue('metadata.transportationData', _value)
                      }
                      value={transportationData ?? []}
                    />
                  </Box>
                  <Box flex={1}>
                    <TagsInput
                      label="Números de brincos"
                      placeholder="--"
                      onChange={(_value) =>
                        setValue('metadata.cattleData', _value)
                      }
                      value={cattleData ?? []}
                    />
                  </Box>
                </Box>
              )}

              <Box
                width="100%"
                alignItems="flex-end"
                direction="column"
                gap={2}
              >
                <Box gap={2}>
                  <Button variant="outlined" onClick={() => setOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit">Salvar</Button>
                </Box>
              </Box>
            </Box>
          </form>
        </Box>
      </Modal>
    </>
  )
}
